import axios from 'axios';
import { AUTH_TOKEN, NETWORK_ERROR } from '../constants/projectConstants';
import { captureSentryException, getLocalStorage, wait } from './jsHelper';
import { navigate, sessionDestroy } from './projectHelper.js';
import { errorToast, infoToast, warningToast } from './toastHelper.js';
import { API_ROOT_URL, GET_ACCESS_REFRESH_TOKEN, GET_GLOBAL_TAGS } from '../helpers/api';
import _ from 'lodash';
import { SIGN_IN_ROUTE } from '../constants/routePathConstants';
import { LogConsole } from './logEvents.js';


class ApiClass {
  _url = '';
  _data = {};
  _method = '';
  _badRequest = null;
  _authFail = null;
  _accessDenied = null;
  _notFound = null;
  _serverError = null;
  _success = null;
  _error = null;
  _query = null;
  _progress = null;
  _api_root = null;
  _headers = {
    'Content-Type': 'application/json',
  };
  _responseType = null;
  setHeaders = (header) => {
    this._headers = { ...this._headers, ...header };
    return this;
  };
  root = (root) => {
    this._api_root = root;
    return this;
  };
  get = (path) => {
    this._method = 'GET';
    this._url = this._api_root + path;
    return this;
  };

  getBlob = (path) => {
    this._method = 'GET';
    this._url = this._api_root + path;
    this._headers = { 'Content-type': 'text/plain' };
    this._responseType = 'arraybuffer';
    this._data = undefined;
    this._progress = undefined;
    return this;
  };

  getBlobNormal = (path) => {
    this._method = 'GET';
    this._url = this._api_root + path;
    this._headers = {};
    // this._responseType = 'arraybuffer';
    // this._data = undefined;
    // this._progress = undefined;
    return this;
  };

  postBlob = (path, payload) => {
    this._method = 'POST';
    this._url = this._api_root + path;
    this._headers = { 'Content-type': 'application/json' };
    this._responseType = 'arraybuffer';
    this._data = payload;
    this._progress = undefined;
    return this;
  };

  getForm = (path) => {
    this._method = 'GET';
    this._url = this._api_root + path;
    this._headers = { 'Content-Type': 'application/json' };
    return this;
  };

  post = (path) => {
    this._method = 'POST';
    this._url = this._api_root + path;
    return this;
  };
  put = (path) => {
    this._method = 'PUT';
    this._url = this._api_root + path;
    return this;
  };
  patch = (path) => {
    this._method = 'PATCH';
    this._url = this._api_root + path;
    return this;
  };
  onUploadProgress = (callback = null) => {
    this._progress = callback;
    return this;
  };
  onDownloadProgress = (callback = null) => {
    this._downloadProgress = callback;
    return this;
  };
  requestConfig = (rc) => {
    this._requestConfig = rc;
    return this;
  };
  delete = (path) => {
    this._method = 'DELETE';
    this._url = this._api_root + path;
    return this;
  };
  success = (callback = null) => {
    this._success = callback;
    return this;
  };
  error = (callback = null) => {
    this._error = callback;
    return this;
  };

  anyError = (callback = null) => {
    this._anyError = callback;
    return this;
  };

  finally = (callback = null) => {
    this._finally = callback;
    return this;
  };

  badRequest400 = (callback = null) => {
    this._badRequest = callback;
    return this;
  };

  authFail401 = (callback = null) => {
    this._authFail = callback;
    return this;
  };

  networkError = (callback = null) => {
    this._networkError = callback;
    return this;
  };

  accessDenied403 = (callback = null) => {
    this._accessDenied = callback;
    return this;
  };

  notFound404 = (callback = null) => {
    this._notFound = callback;
    return this;
  };

  serverErr500 = (callback = null) => {
    this._serverError = callback;
    return this;
  };

  data = (a) => {
    if (this._query) {
      this._data['variables'] = a;
    } else {
      this._data = a;
    }
    return this;
  };
  upload = (callback = null) => {
    this._headers = {
      'Content-type': 'multipart/form-data',
    };
    return this.send(callback);
  };

  _isRefreshing = false;
  _refreshSubscribers = [];
  isDeactiveError = false;

  onRefreshed = (token) => {
    this._refreshSubscribers.forEach((callback) => callback(token));
    this._refreshSubscribers = [];
    this._isRefreshing = false;
  };

  addRefreshSubscriber = (callback) => {
    this._refreshSubscribers.push(callback);
  };

  refreshToken = async (refresh) => {
    const myHeaders = new Headers();
    myHeaders.append('Content-Type', 'application/json');

    const raw = JSON.stringify({
      refresh: refresh,
    });

    const requestOptions = {
      method: 'POST',
      headers: myHeaders,
      body: raw,
      redirect: 'follow',
    };

    return await fetch(`${API_ROOT_URL}${GET_ACCESS_REFRESH_TOKEN}`, requestOptions)
      .then((response) => {
        if (response.ok) {
          return response.json();
        } else {
          // return 'Token expired!!!'
          throw 'Token expired!!!';
        }
      })
      .then((result) => result);
  };

  send = async (callback = null) => {
    // if (!this._api_root) {
    //   throw new Error('root path missing');
    // }

    const token = getLocalStorage(AUTH_TOKEN);
    let res = null;
    let err = null;
    const axiosVariable = axios({
      method: this._method,
      url: this._url,
      data: this._data,
      responseType: this._responseType,
      headers: { ...this._headers, Authorization: `Bearer ${token}` },
      onUploadProgress: this._progress,
      onDownloadProgress: this._downloadProgress,
    });

    axios.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token && config?.headers?.Authorization) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );


    // Add a response interceptor to refresh the JWT token if it's expired
    axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        const refreshToken = localStorage.getItem('refresh_token');

        // logging out the user if deactivated
        if (error?.response?.data?.message?.message?.toLowerCase()?.includes("member is not active") && !this.isDeactiveError) {
          this.isDeactiveError = true;
          errorToast('You are no longer active in this organization. Please connect with your admin.');
          await wait(2000);
          sessionDestroy(null, false);
          navigate(SIGN_IN_ROUTE);
          return;
        }
        if (!refreshToken && !this.isDeactiveError) {
          handleErrorMessageFromAPIFailer(error);
          return;
        }
        if (error?.response?.status === 401 && !originalRequest._retry && refreshToken) {
          if (!this._isRefreshing) {
            this._isRefreshing = true;
            try {
              const data = await this.refreshToken(refreshToken);
              const access = data?.access;
              const refresh = data?.refresh;
              if (!access || !refresh) {
                sessionDestroy(null, false);
                navigate(SIGN_IN_ROUTE);
                return;
              }
              localStorage.setItem('token', access);
              localStorage.setItem('refresh_token', refresh);
              if (originalRequest?.headers?.Authorization) {
                originalRequest.headers.Authorization = `Bearer ${access}`;
              }
              this.onRefreshed(access);
              return await axios(originalRequest);
            } catch (error_1) {
              if (error_1 === 'Token expired!!!' || error_1?.response?.status === 401) {
                localStorage.removeItem('token');
                localStorage.removeItem('refresh_token');
                // errorToast('Authentication failed', { toastId: '401_ERROR' });
                errorToast('Token expired!!!', { toastId: '401_ERROR' });
                await wait(1000);
                sessionDestroy(null, false);
                navigate(SIGN_IN_ROUTE);
              }
              return await Promise.reject(error_1);
            }
          } else {
            return new Promise((resolve) => {
              this.addRefreshSubscriber((token) => {
                if (originalRequest?.headers?.Authorization) {
                  originalRequest.headers.Authorization = `Bearer ${token}`;
                }
                resolve(axios(originalRequest));
              });
            });
          }
        }
        return Promise.reject(error);
      }
    );

    const result = await axiosVariable
      .then((r) => {
        res = r;
        if (r?.data?.status || r?.data?.status === undefined) {
          this._success?.call(this, res.data || res);
        } else {
          // throw { response: { data: res.data } }; TODO: THIS LINE IS SCREWING UP MY API CALLS WE HAVE TO FIND A BETTER WAY TO HANDLE THIS
        }
        return res.data;
      })
      .catch((e) => {
        this?._anyError?.(e);

        err = e;
        if (!err?.response && err.toString().includes(NETWORK_ERROR)) {
          if (this._networkError) {
            this._networkError();
            return;
          } else {
            !this._requestConfig?.messages?.hideTost &&
              infoToast('Network Error', { toastId: 'NETWORK_ERROR' });
            LogConsole('Network error. Please check you internet and try once again');
            // callback?.call(this, err?.response, err?.response?.status, res);
            return;
          }
        }
        const data = err?.response?.data ?? {};
        const { message: msg = '' } = data;
        const { status } = e?.response ?? {};
        let errorExec = true;
        const tags = {
          'api_url': this._url,
          'api_method': this._method,
          'api_status': status,
          'api_message': msg,
          // "issue.type": "warning",
        }

        switch (status) {
          case 400:
            captureSentryException({
              type: "400",
              message: msg,
              url: this._url,
            }, { tags })

            this._badRequest?.call(this, data);
            if (!this._badRequest) {
              const customMessage = this._requestConfig?.messages[400];
              const hideTost = this._requestConfig?.messages?.hideTost;

              if (customMessage) {
                !hideTost && errorToast(customMessage);
                break;
              }

              try {
                if (msg && _.isString(msg)) {
                  !hideTost && errorToast(msg);
                  break;
                } else {
                  const { non_field_errors } = msg;
                  if (_.isString(non_field_errors)) {
                    !hideTost && errorToast(non_field_errors);
                    break;
                  }
                  console.log('may need to add additional destructured keys to 400 requests', msg);
                  !this._requestConfig?.messages?.hideTost && handleErrorMessageFromAPIFailer(msg)
                }
              } catch {
                console.log('need to add additional destructured keys to 400 requests', msg);
                !this._requestConfig?.messages?.hideTost && handleErrorMessageFromAPIFailer(msg)
              }
            }
            break;
          case 403: //session ok but access prevent
            captureSentryException({
              type: "403",
              message: msg,
              url: this._url,
            }, { tags })
            this._accessDenied?.call(this, data);
            if (!this._accessDenied) {
              const customMessage = this._requestConfig?.messages?.[403];
              const message = customMessage || 'Permission Denied';
              !this._requestConfig?.messages?.hideTost && warningToast(message, { toastId: '403_ERROR' });
            }
            errorExec = false;
            break;
          case 404: //not found path
            captureSentryException({
              type: "404",
              message: msg,
              url: this._url,
            }, { tags })
            this._notFound?.call(this, data);
            if (!this._notFound) {
              const detailMessage =
              typeof data?.message?.detail === 'string'
                ? data.message.detail
                : undefined;
              const customMessage = this._requestConfig?.messages?.[400] || detailMessage;
              window.$toastMessage = customMessage || 'Request Not Found'
              !this._requestConfig?.messages?.hideTost && errorToast(customMessage || 'Request Not Found', { toastId: '404_ERROR' });
            }
            errorExec = false;
            break;
          case 500: //internal server error
            captureSentryException({
              type: "500",
              message: msg,
              url: this._url
            }, { tags })
            this._serverError?.call(this, data);
            if (!this._serverError) {
              !this._requestConfig?.messages?.hideTost &&
                errorToast('Internal Server Error', { toastId: '500_ERROR' });
              this._error?.call(this, err?.response?.data ?? {});
            }
            errorExec = false;
            break;
          default:
            break;
        }

        if (this._error && errorExec) {
          this._error(err?.response?.data ?? {}, { status: err?.response?.status });
        }
      })
      .finally(() => {
        this._finally?.();
      });
    if (callback && (res || err?.response)) {
      callback?.call(this, err?.response, err?.response?.status, res);
    }
    return result;
  };
}
const api = () => new ApiClass();

const MAX_CACHE_SERVE_COUNT = 20;
let cache = {
  global_tags: { list: null, serve_count: 0 },
};

function cachedList(urlPath, key = null) {
  if (key === null) key = urlPath;
  if (cache[key].list !== null && cache[key].serve_count < MAX_CACHE_SERVE_COUNT) {
    cache[key].serve_count += 1;
    return new Promise((resolve, reject) => {
      resolve(cache[key].list);
    });
  }
  return apiFetch(urlPath)
    .send()
    .then((response) => {
      cache[key].list = response.results;
      cache[key].serve_count = 0;
      return new Promise((resolve, reject) => {
        resolve(cache[key].list);
      });
    });
}

export const getJson = async (url, noCache = false) => {
  const options = {};
  
  if (noCache) {
    // Using 'no-store' prevents caching altogether.
    // Alternatively, you can use 'no-cache' if you want the browser to validate the cache.
    options.cache = "no-store";
  }
  
  const res = await fetch(url, options);
  return res.json();
};

export const apiFetch = (url) => {
  return api().root(API_ROOT_URL).get(url);
};
export const apiPost = (url, payload) => {
  return api().root(API_ROOT_URL).post(url).data(payload);
};
export const apiGetCachedList = async (key) => {
  let URL = '';
  switch (key) {
    case 'global_tags':
      URL = GET_GLOBAL_TAGS;
      break;
    default:
      break;
  }
  let results = cachedList(URL, key);
  // double checking it's not an unresolved promise since these are somewhat nested
  if (results && _.isFunction(results.then)) {
    await results.then((res) => {
      results = res;
    });
  }
  results = _.map(results, (l) => {
    l.value = l.id;
    l.label = l.name;
    return l;
  });
  return results;
};
export const apiErrorHandler = (error, label = null) => {
  const { message } = error;

  console.log(label ? `Error at ${label}:` : 'apiErrorHandler:', error);
  let errorMessage = typeof message === 'string' ? message : message?.detail;

  if (!errorMessage && typeof message === 'object' && !message?.detail) {
    errorMessage = '';

    Object.values(message).forEach((messageArr) => {
      errorMessage += ` ${messageArr.join(' ')}`;
    });
  }
  if (errorMessage) {
    errorToast(errorMessage);
  }
};

// handling the api response error
export const handleErrorMessageFromAPIFailer = (res) => {

  // Manage the tag API error.
  // Get the first key in the object
  const firstKey = res && Object.keys(res)?.[0];
  // Get the first message from that key's array
  const errorMessage = res?.[firstKey?.toString()]?.[0] || typeof res?.[firstKey?.toString()] === 'string' ? res?.[firstKey?.toString()] : '';
  if(errorMessage){
    errorToast(errorMessage);
    return;
  }

  // Manage the Captcha API error message
  if (typeof res?.response?.data?.error === 'object' && res?.response?.data?.error?.['error-codes']?.length) {
    errorToast(
      res?.response?.data?.error?.['error-codes']?.[0] ? `${res?.response?.data?.error?.['error-codes']?.[0]}. Captcha verification failed. Please try again` :
        'Captcha verification failed. Please try again!'
    );
    return;
  }

  if (typeof res?.response?.data?.error === 'string') {
    errorToast(
      res?.response?.data?.error || 'Captcha verification failed. Please try again!'
    );
    return;
  }

  if (res) {
    if (res?.response?.data && !res?.response?.data?.status) {
      if (typeof res?.response?.data?.message === 'object') {
        const key = Object.keys(res?.response?.data?.message);
        errorToast(
          res?.response?.data?.message?.[key]?.[0]?.replace('This', key?.[0]) ||
          'Something went wrong!'
        );
      } else if (typeof res?.response?.data?.message === 'string') {
        errorToast(res?.response?.data?.message || res.data?.message || 'Something went wrong!');
      } else {
        errorToast(res?.response?.data?.message || res.data?.message || 'Something went wrong!');
      }
    } else {
      if (typeof res?.message === 'object') {
        const key = Object.keys(res?.message);
        errorToast(
          res?.message?.[key]?.[0]?.replace('This', key?.[0]) || 'Something went wrong!'
        );
      } else if (typeof res?.message === 'string') {
        errorToast(res?.message || res.data?.message || 'Something went wrong!');
      } else {
        errorToast(res?.message || res.data?.message || 'Something went wrong!');
      }
    }
  }
};

export default api;
